import { useState, useEffect, useCallback, useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from "@/components/ui/dropdown-menu";
import { 
  Sparkles, Copy, Check, Loader2, History, Settings, 
  Trash2, Clock, MessageSquare, Save, Eye, EyeOff, KeyRound, User,
  TrendingUp, Shield, LogOut, FileText, Zap, Users, Target,
  Megaphone, Lightbulb, Wrench, Search, ChevronRight, ArrowRight,
  ChevronDown, Building2, Home
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BS_LOGO = "https://customer-assets.emergentagent.com/job_custom-chat-proxy/artifacts/2khpbzo9_blackswan_center_logo.png";

// Role constants
const ROLE_ADMIN = "admin";
const ROLE_MANAGER = "manager";
const ROLE_USER = "user";

// Toast configuration - extracted to avoid inline object
const TOAST_OPTIONS = {
  style: { 
    background: '#18181B', 
    border: '1px solid rgba(196, 30, 58, 0.2)', 
    color: '#fff',
    fontFamily: 'Manrope, sans-serif'
  }
};

const iconMap = {
  TrendingUp, Shield, FileText, Zap, Users, Target,
  Megaphone, Lightbulb, MessageSquare, Settings, Wrench, Search,
  Building2, Home
};

// ==================== HELPER COMPONENTS ====================
// Extracted to remove nested ternaries and improve readability

const ResponseDisplay = ({ loading, response }) => {
  if (loading) {
    return (
      <div className="response-section p-4 md:p-6 min-h-[150px] md:min-h-[200px]" data-testid="response-section">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <span className="typing-dot" />
            <span className="typing-dot" />
            <span className="typing-dot" />
          </div>
          <span className="text-neutral-500 text-sm">Generating response...</span>
        </div>
      </div>
    );
  }
  
  if (response) {
    return (
      <div className="response-section p-4 md:p-6 min-h-[150px] md:min-h-[200px]" data-testid="response-section">
        <p className="text-neutral-300 text-sm md:text-base leading-relaxed whitespace-pre-wrap animate-fade-in" data-testid="response-text">
          {response}
        </p>
      </div>
    );
  }
  
  return (
    <div className="response-section p-4 md:p-6 min-h-[150px] md:min-h-[200px]" data-testid="response-section">
      <p className="text-neutral-600 text-xs md:text-sm italic">
        Your AI-generated response will appear here...
      </p>
    </div>
  );
};

const HistoryContent = ({ loading, history, isAdmin, formatDate, handleDelete }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#C41E3A]" />
      </div>
    );
  }
  
  if (history.length === 0) {
    return (
      <Card className="admin-card">
        <CardContent className="flex flex-col items-center justify-center py-12 md:py-20">
          <MessageSquare className="w-10 h-10 md:w-12 md:h-12 text-neutral-700 mb-4" />
          <p className="text-neutral-400 text-base md:text-lg font-medium">No history yet</p>
          <p className="text-neutral-600 text-xs md:text-sm">Generate responses to see them here</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <ScrollArea className="h-[500px] md:h-[600px] pr-2 md:pr-4">
      <div className="space-y-3 md:space-y-4">
        {history.map((item) => (
          <Card key={item.id} className="history-card group">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <span className="text-xs text-neutral-500 flex items-center gap-2">
                  <Clock className="w-3 h-3" />
                  {formatDate(item.timestamp)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(item.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-neutral-500 hover:text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-2">Input</p>
                  <p className="text-neutral-300 text-sm">{item.input_text}</p>
                </div>
                <div className="border-t border-white/5 pt-4">
                  <p className="text-xs font-bold uppercase tracking-widest text-[#C41E3A] mb-2">Response</p>
                  <p className="text-neutral-400 text-sm whitespace-pre-wrap">{item.response_text}</p>
                </div>
                {isAdmin && item.username && (
                  <div className="border-t border-white/5 pt-3 mt-3">
                    <span className="text-xs text-neutral-600">
                      Generated by: <span className="text-neutral-400">{item.username}</span>
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </ScrollArea>
  );
};

// ==================== AUTH HOOKS ====================
const useAuth = () => {
  const [tool, setTool] = useState(() => {
    const saved = localStorage.getItem('currentTool');
    return saved ? JSON.parse(saved) : null;
  });
  const [credentials, setCredentials] = useState(() => {
    const saved = localStorage.getItem('toolCredentials');
    return saved ? JSON.parse(saved) : null;
  });
  const [userRole, setUserRole] = useState(() => {
    const saved = localStorage.getItem('userRole');
    return saved || ROLE_USER;
  });

  const login = (toolData, creds, role = ROLE_USER) => {
    setTool(toolData);
    setCredentials(creds);
    setUserRole(role);
    localStorage.setItem('currentTool', JSON.stringify(toolData));
    localStorage.setItem('toolCredentials', JSON.stringify(creds));
    localStorage.setItem('userRole', role);
  };

  const logout = () => {
    setTool(null);
    setCredentials(null);
    setUserRole(ROLE_USER);
    localStorage.removeItem('currentTool');
    localStorage.removeItem('toolCredentials');
    localStorage.removeItem('userRole');
  };

  const isAdmin = userRole === ROLE_ADMIN;
  const isManager = userRole === ROLE_MANAGER;
  const canViewHistory = true; // All roles can view their own history
  const canClearHistory = userRole === ROLE_ADMIN || userRole === ROLE_MANAGER;

  return { tool, credentials, userRole, login, logout, isLoggedIn: !!tool, isAdmin, isManager, canViewHistory, canClearHistory };
};

const useConfigAuth = () => {
  const [isConfigAdmin, setIsConfigAdmin] = useState(() => {
    return localStorage.getItem('isConfigAdmin') === 'true';
  });
  const [configCredentials, setConfigCredentials] = useState(() => {
    const saved = localStorage.getItem('configCredentials');
    return saved ? JSON.parse(saved) : null;
  });

  const loginConfig = (creds) => {
    setIsConfigAdmin(true);
    setConfigCredentials(creds);
    localStorage.setItem('isConfigAdmin', 'true');
    localStorage.setItem('configCredentials', JSON.stringify(creds));
  };

  const logoutConfig = () => {
    setIsConfigAdmin(false);
    setConfigCredentials(null);
    localStorage.removeItem('isConfigAdmin');
    localStorage.removeItem('configCredentials');
  };

  return { isConfigAdmin, configCredentials, loginConfig, logoutConfig };
};

// ==================== TOOL LOGIN PAGE ====================
const ToolLoginPage = ({ onLogin, selectedBusinessUnit }) => {
  const [tools, setTools] = useState([]);
  const [selectedTool, setSelectedTool] = useState(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [groupedTools, setGroupedTools] = useState({});
  const navigate = useNavigate();

  // Fetch tools on mount
  useEffect(() => {
    const fetchTools = async () => {
      try {
        const res = await axios.get(`${API}/tools`);
        setTools(res.data);
        // Group tools by department within each business unit
        const grouped = res.data.reduce((acc, tool) => {
          const buKey = tool.business_unit || 'corporate_uae';
          if (!acc[buKey]) acc[buKey] = {};
          if (!acc[buKey][tool.department]) acc[buKey][tool.department] = [];
          acc[buKey][tool.department].push(tool);
          return acc;
        }, {});
        setGroupedTools(grouped);
      } catch (err) {
        toast.error("Failed to load tools");
      }
    };
    fetchTools();
  }, []);

  // Clear selected tool when business unit changes
  useEffect(() => {
    setSelectedTool(null);
    setUsername("");
    setPassword("");
  }, [selectedBusinessUnit]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!selectedTool) {
      toast.error("Please select a tool");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/tool/login`, {
        tool_id: selectedTool.id,
        username,
        password
      });
      // Pass role from login response
      const userRole = res.data.user?.role || ROLE_USER;
      onLogin(res.data.tool, { username, password }, userRole);
      navigate('/workspace');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const Icon = selectedTool ? (iconMap[selectedTool.icon] || Sparkles) : Sparkles;
  const deptNames = { sales: "Sales", operations: "Operations", admin: "Admin", realestate: "Real Estate" };
  const deptOrder = ['sales', 'operations', 'admin', 'realestate'];
  
  // Get current business unit's tools
  const currentBuKey = selectedBusinessUnit?.id || 'corporate_uae';
  const currentBuTools = groupedTools[currentBuKey] || {};

  return (
    <main className="min-h-[calc(100vh-140px)] relative" data-testid="login-page">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black to-[#0a0a0a]" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[radial-gradient(ellipse_at_center,rgba(196,30,58,0.08)_0%,transparent_70%)]" />
      
      <div className="relative z-10 px-4 md:px-12 lg:px-20 py-8 md:py-12">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-8 md:mb-16">
            <img src={BS_LOGO} alt="BlackSwan" className="h-14 md:h-20 mx-auto mb-4 md:mb-8" />
            <h1 className="font-heading text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-3 md:mb-4 uppercase tracking-tight px-2">
              {selectedBusinessUnit?.name || 'AI Tools'}
            </h1>
            <p className="text-neutral-400 text-sm md:text-lg max-w-xl mx-auto px-4">
              Select your tool and sign in to access AI-powered business intelligence
            </p>
          </div>

          {/* Tools Grid - Show only selected business unit's departments */}
          <div className="space-y-8 md:space-y-10 mb-8 md:mb-16">
            {deptOrder.map(dept => currentBuTools[dept] && (
              <div key={dept} className="animate-fade-in">
                <div className="flex items-center gap-3 mb-4 md:mb-6">
                  <div className="w-1.5 md:w-2 h-6 md:h-8 bg-[#C41E3A]" />
                  <h2 className="dept-label text-white text-xs md:text-sm">
                    {deptNames[dept]}
                  </h2>
                  <div className="flex-1 h-px bg-gradient-to-r from-white/10 to-transparent" />
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                  {currentBuTools[dept].map((tool) => {
                    const ToolIcon = iconMap[tool.icon] || Sparkles;
                    const isSelected = selectedTool?.id === tool.id;
                    
                    return (
                      <button
                        key={tool.id}
                        onClick={() => setSelectedTool(tool)}
                        className={`tool-card p-4 md:p-6 text-left group ${isSelected ? 'border-[#C41E3A] red-glow' : ''}`}
                        data-testid={`tool-select-${tool.id}`}
                      >
                        <div className="icon-container w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-3 md:mb-4">
                          <ToolIcon className="w-4 h-4 md:w-5 md:h-5 text-[#C41E3A]" />
                        </div>
                        <h3 className="font-heading font-bold text-white text-base md:text-lg mb-1 md:mb-2 uppercase tracking-wide">
                          {tool.name}
                        </h3>
                        <p className="text-neutral-500 text-xs md:text-sm leading-relaxed">
                          {tool.subtitle}
                        </p>
                        <div className="mt-3 md:mt-4 flex items-center text-[#C41E3A] opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
                          <span className="text-xs font-bold uppercase tracking-wider">Select</span>
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Login Form */}
          {selectedTool && (
            <div id="login-form-section" className="max-w-sm md:max-w-md mx-auto animate-fade-in px-2">
              <Card className="admin-card overflow-hidden">
                <div className="h-1 bg-gradient-to-r from-transparent via-[#C41E3A] to-transparent" />
                <CardHeader className="text-center pt-6 md:pt-8 pb-3 md:pb-4 px-4 md:px-6">
                  <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 flex items-center justify-center bg-gradient-to-br from-[#C41E3A] to-[#8A1529] red-glow">
                    <Icon className="w-5 h-5 md:w-7 md:h-7 text-white" />
                  </div>
                  <CardTitle className="font-heading text-lg md:text-2xl text-white uppercase tracking-wide">
                    {selectedTool.name}
                  </CardTitle>
                  <p className="text-neutral-500 text-xs md:text-sm mt-2">Enter credentials to continue</p>
                </CardHeader>
                <CardContent className="pb-6 md:pb-8 px-4 md:px-6">
                  <form onSubmit={handleLogin} className="space-y-4 md:space-y-5">
                    <div className="space-y-2">
                      <Label className="text-xs font-bold uppercase tracking-widest text-neutral-500">Username</Label>
                      <Input
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="Enter username"
                        className="form-input h-11 md:h-12"
                        data-testid="login-username"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold uppercase tracking-widest text-neutral-500">Password</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter password"
                          className="form-input h-11 md:h-12 pr-12"
                          data-testid="login-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white transition-colors"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="bs-btn w-full h-11 md:h-12 mt-2" 
                      disabled={loading}
                      data-testid="login-submit"
                    >
                      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                      Sign In
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Config Admin Link */}
          <div className="text-center mt-8 md:mt-12">
            <Link to="/config" className="inline-flex items-center gap-2 text-neutral-600 text-sm hover:text-[#C41E3A] transition-colors">
              <Wrench className="w-4 h-4" />
              <span className="font-medium uppercase tracking-wider text-xs">Config Admin</span>
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
};

// ==================== WORKSPACE PAGE ====================
const WorkspacePage = ({ tool, credentials, onLogout }) => {
  const [input, setInput] = useState("");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!tool) navigate('/');
  }, [tool, navigate]);

  const handleGenerate = async () => {
    if (!input.trim()) {
      toast.error("Please enter some text");
      return;
    }

    setLoading(true);
    setResponse("");

    try {
      const res = await axios.post(`${API}/tool/generate?tool_id=${tool.tool_id}`, { 
        text: input,
        username: credentials?.username || 'anonymous'
      });
      setResponse(res.data.response);
      toast.success("Response generated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate response");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (response) {
      await navigator.clipboard.writeText(response);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!tool) return null;

  return (
    <main className="min-h-[calc(100vh-140px)] relative" data-testid="workspace-page">
      <div className="absolute inset-0 bg-gradient-to-b from-black to-[#050505]" />
      <div className="absolute top-0 right-0 w-[600px] h-[400px] bg-[radial-gradient(ellipse_at_top_right,rgba(196,30,58,0.05)_0%,transparent_60%)]" />
      
      <div className="relative z-10 px-4 md:px-12 lg:px-20 py-6 md:py-12">
        <div className="max-w-4xl mx-auto">
          <div className="section-card p-4 md:p-8 lg:p-10">
            {/* Input */}
            <div className="space-y-3 md:space-y-4 mb-6 md:mb-8">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Enter your query or content here..."
                className="elegant-input min-h-[140px] md:min-h-[180px] resize-none text-sm md:text-base p-4 md:p-5 text-white"
                data-testid="workspace-input"
              />
              <Button
                onClick={handleGenerate}
                disabled={loading || !input.trim()}
                className="bs-btn w-full h-12 md:h-14 text-sm md:text-base"
                data-testid="generate-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 md:w-5 md:h-5 mr-2 animate-spin" />
                    Processing
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                    Generate Response
                  </>
                )}
              </Button>
            </div>

            {/* Divider */}
            <div className="red-divider my-6 md:my-8" />

            {/* Response */}
            <div className="space-y-3 md:space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-widest text-neutral-500">Output</span>
                {response && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopy}
                    className="text-neutral-500 hover:text-[#C41E3A] transition-colors h-8 px-2"
                    data-testid="copy-btn"
                  >
                    {copied ? <Check className="w-4 h-4 mr-1 md:mr-2" /> : <Copy className="w-4 h-4 mr-1 md:mr-2" />}
                    <span className="text-xs uppercase tracking-wider">{copied ? "Copied" : "Copy"}</span>
                  </Button>
                )}
              </div>
              <ResponseDisplay loading={loading} response={response} />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

// ==================== HISTORY PAGE ====================
const ToolHistoryPage = ({ tool, credentials, userRole }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const isAdmin = userRole === ROLE_ADMIN;

  const fetchHistory = useCallback(async () => {
    if (!tool) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        username: credentials?.username || '',
        role: userRole || ROLE_USER
      });
      const res = await axios.get(`${API}/tool/${tool.tool_id}/history?${params}`);
      setHistory(res.data);
    } catch (err) {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [tool, credentials?.username, userRole]);

  useEffect(() => {
    if (!tool) {
      navigate('/');
      return;
    }
    fetchHistory();
  }, [tool, navigate, fetchHistory]);

  const handleDelete = async (id) => {
    try {
      const params = new URLSearchParams({
        username: credentials?.username || '',
        role: userRole || ROLE_USER
      });
      await axios.delete(`${API}/tool/${tool.tool_id}/history/${id}?${params}`);
      setHistory(history.filter(h => h.id !== id));
      toast.success("Entry deleted");
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm("Clear all history?")) return;
    try {
      const params = new URLSearchParams({
        username: credentials?.username || '',
        role: userRole || ROLE_USER
      });
      await axios.delete(`${API}/tool/${tool.tool_id}/history?${params}`);
      setHistory([]);
      toast.success("History cleared");
    } catch (error) {
      toast.error("Failed to clear");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (!tool) return null;

  return (
    <main className="min-h-[calc(100vh-140px)] relative" data-testid="history-page">
      <div className="absolute inset-0 bg-gradient-to-b from-black to-[#050505]" />
      
      <div className="relative z-10 px-4 md:px-12 lg:px-20 py-6 md:py-12">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-white uppercase tracking-tight">
                {isAdmin ? "All History" : "My History"}
              </h1>
              <p className="text-neutral-500 text-xs md:text-sm mt-1">
                {isAdmin ? "All user generations" : "Your previous generations"}
              </p>
            </div>
            {history.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearAll}
                className="bs-btn-secondary w-full sm:w-auto"
                data-testid="clear-history-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            )}
          </div>

          <HistoryContent 
            loading={loading} 
            history={history} 
            isAdmin={isAdmin} 
            formatDate={formatDate} 
            handleDelete={handleDelete} 
          />
        </div>
      </div>
    </main>
  );
};

// ==================== CONFIG LOGIN ====================
const ConfigLoginPage = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/config/login`, { username, password });
      onLogin({ username, password });
      navigate('/config/dashboard');
    } catch (error) {
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-140px)] flex items-center justify-center relative">
      <div className="absolute inset-0 bg-gradient-to-b from-black to-[#0a0a0a]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(196,30,58,0.05)_0%,transparent_60%)]" />
      
      <div className="relative z-10 w-full max-w-sm md:max-w-md px-4 md:px-6">
        <Card className="admin-card overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-transparent via-[#C41E3A] to-transparent" />
          <CardHeader className="text-center pt-6 md:pt-8 px-4 md:px-6">
            <img src={BS_LOGO} alt="BlackSwan" className="h-12 md:h-14 mx-auto mb-4 md:mb-6" />
            <CardTitle className="font-heading text-xl md:text-2xl text-white uppercase tracking-wide">Config Admin</CardTitle>
            <p className="text-neutral-500 text-xs md:text-sm mt-2">Manage all BlackSwan tools</p>
          </CardHeader>
          <CardContent className="pb-6 md:pb-8 px-4 md:px-6">
            <form onSubmit={handleLogin} className="space-y-4 md:space-y-5">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-neutral-500">Username</Label>
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Admin username"
                  className="form-input h-11 md:h-12"
                  data-testid="config-username"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-neutral-500">Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Admin password"
                    className="form-input h-11 md:h-12"
                    data-testid="config-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="bs-btn w-full h-11 md:h-12" disabled={loading} data-testid="config-login-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                Sign In
              </Button>
            </form>
            <div className="text-center mt-5 md:mt-6">
              <Link to="/" className="text-neutral-600 text-xs md:text-sm hover:text-[#C41E3A] transition-colors">
                ← Back to Tools
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
};

// ==================== CONFIG DASHBOARD ====================
const ConfigDashboard = ({ credentials, onLogout }) => {
  const [tools, setTools] = useState([]);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const fetchTools = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/config/tools`);
      setTools(res.data);
    } catch (err) {
      toast.error("Failed to load tools");
    }
  }, []);

  useEffect(() => {
    if (!credentials) {
      navigate('/config');
      return;
    }
    fetchTools();
  }, [credentials, navigate, fetchTools]);

  const handleSaveInstruction = async (tool) => {
    setSaving(true);
    try {
      await axios.put(`${API}/config/tool/${tool.tool_id}/instruction`, {
        name: tool.name,
        subtitle: tool.subtitle,
        instruction: tool.instruction
      });
      toast.success("Tool updated");
      fetchTools();
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCredentials = async (toolId, newUsername, newPassword) => {
    if (newUsername.length < 3 || newPassword.length < 6) {
      toast.error("Username min 3, password min 6 chars");
      return;
    }
    try {
      await axios.put(`${API}/config/tool/${toolId}/credentials`, {
        tool_id: toolId,
        new_username: newUsername,
        new_password: newPassword
      });
      toast.success("Credentials updated");
      fetchTools();
    } catch {
      toast.error("Failed to update");
    }
  };

  const updateTool = (toolId, field, value) => {
    setTools(tools.map(t => t.tool_id === toolId ? { ...t, [field]: value } : t));
  };

  if (!credentials) return null;

  const deptNames = { sales: "Sales", operations: "Operations", admin: "Admin" };

  return (
    <main className="min-h-[calc(100vh-140px)] relative" data-testid="config-dashboard">
      <div className="absolute inset-0 bg-gradient-to-b from-black to-[#050505]" />
      
      <div className="relative z-10 px-6 md:px-12 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold text-white uppercase tracking-tight">Configuration</h1>
              <p className="text-neutral-500 text-sm mt-1">Manage all BlackSwan tools</p>
            </div>
            <Button variant="outline" size="sm" onClick={onLogout} className="bs-btn-secondary">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>

          <div className="red-divider mb-8" />

          <ScrollArea className="h-[calc(100vh-280px)]">
            <div className="space-y-6 pr-4">
              {tools.map((tool) => {
                const Icon = iconMap[tool.icon] || Sparkles;
                return (
                  <Card key={tool.tool_id} className="admin-card">
                    <CardHeader className="pb-4">
                      <CardTitle className="flex items-center gap-3 text-white">
                        <div className="icon-container w-10 h-10 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-[#C41E3A]" />
                        </div>
                        <div>
                          <span className="font-heading text-lg uppercase">{tool.name}</span>
                          <span className="text-neutral-500 text-xs ml-2">({deptNames[tool.department]})</span>
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Credentials */}
                      <div className="bg-black/30 p-4 border border-white/5">
                        <p className="text-xs font-bold uppercase tracking-widest text-[#C41E3A] mb-4">Credentials</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <Label className="text-xs text-neutral-500">Current</Label>
                            <p className="text-white font-mono text-sm mt-1">{tool.current_username}</p>
                          </div>
                          <Input placeholder="New username" className="form-input h-10 text-sm" id={`u-${tool.tool_id}`} />
                          <div className="flex gap-2">
                            <Input type="password" placeholder="New password" className="form-input h-10 text-sm" id={`p-${tool.tool_id}`} />
                            <Button
                              size="sm"
                              onClick={() => {
                                const u = document.getElementById(`u-${tool.tool_id}`).value;
                                const p = document.getElementById(`p-${tool.tool_id}`).value;
                                handleSaveCredentials(tool.tool_id, u, p);
                              }}
                              className="bs-btn h-10 px-4"
                            >
                              <Save className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Tool Config */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-2 block">Name</Label>
                          <Input value={tool.name} onChange={(e) => updateTool(tool.tool_id, 'name', e.target.value)} className="form-input" />
                        </div>
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-2 block">Subtitle</Label>
                          <Input value={tool.subtitle} onChange={(e) => updateTool(tool.tool_id, 'subtitle', e.target.value)} className="form-input" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-widest text-neutral-500 mb-2 block">AI Instruction</Label>
                        <Textarea
                          value={tool.instruction}
                          onChange={(e) => updateTool(tool.tool_id, 'instruction', e.target.value)}
                          className="form-input min-h-[120px] font-mono text-sm"
                        />
                      </div>
                      <div className="flex justify-end">
                        <Button onClick={() => handleSaveInstruction(tool)} disabled={saving} className="bs-btn">
                          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                          Save Config
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      </div>
    </main>
  );
};

// ==================== HEADER ====================
const Header = ({ tool, isConfigAdmin, onLogout, selectedBusinessUnit, onSelectBusinessUnit, businessUnits }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  return (
    <header className="app-header sticky top-0 z-50 px-4 md:px-12 py-3 md:py-4">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-2 md:gap-6 flex-1">
          {/* Logo - only links home, no tool behavior */}
          <Link to="/" className="flex items-center gap-2 md:gap-4 flex-shrink-0">
            <img src={BS_LOGO} alt="BlackSwan" className="h-8 md:h-10" />
          </Link>
          
          {/* Business Unit Selector - Works on all screen sizes */}
          {!tool && !isConfigAdmin && businessUnits && businessUnits.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="nav-btn flex items-center gap-1 md:gap-2 text-white hover:text-white border border-[#C41E3A]/30 hover:border-[#C41E3A]/50 px-2 md:px-4 h-9 md:h-10"
                  data-testid="business-unit-selector"
                >
                  {selectedBusinessUnit?.id === 'realestate_uae' ? (
                    <Home className="w-4 h-4 text-[#C41E3A]" />
                  ) : (
                    <Building2 className="w-4 h-4 text-[#C41E3A]" />
                  )}
                  <span className="text-[10px] md:text-xs font-medium uppercase tracking-wider truncate max-w-[120px] md:max-w-none">
                    {selectedBusinessUnit?.name || 'Select Unit'}
                  </span>
                  <ChevronDown className="w-3 h-3 opacity-50 flex-shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="start" 
                className="w-72 md:w-80 bg-[#18181B] border-white/10"
              >
                <DropdownMenuLabel className="text-xs uppercase tracking-widest text-[#C41E3A] font-bold">
                  Select Business Unit
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                {businessUnits.map((bu) => {
                  const isSelected = selectedBusinessUnit?.id === bu.id;
                  return (
                    <DropdownMenuItem 
                      key={bu.id}
                      onClick={() => onSelectBusinessUnit(bu)}
                      className={`cursor-pointer py-3 px-3 focus:bg-white/5 group ${isSelected ? 'bg-[#C41E3A]/10' : ''}`}
                      data-testid={`bu-select-${bu.id}`}
                    >
                      <div className="flex items-center gap-3 w-full">
                        <div className={`w-10 h-10 rounded flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-[#C41E3A]/20' : 'bg-white/5 group-hover:bg-white/10'}`}>
                          {bu.id === 'corporate_uae' ? (
                            <Building2 className={`w-5 h-5 ${isSelected ? 'text-[#C41E3A]' : 'text-neutral-400'}`} />
                          ) : (
                            <Home className={`w-5 h-5 ${isSelected ? 'text-[#C41E3A]' : 'text-neutral-400'}`} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${isSelected ? 'text-[#C41E3A]' : 'text-white'}`}>{bu.name}</p>
                          <p className="text-xs text-neutral-500">{bu.tools.length} tools available</p>
                        </div>
                        {isSelected && <Check className="w-4 h-4 text-[#C41E3A]" />}
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        
        {/* Workspace Navigation - when logged into a tool */}
        {tool && (
          <nav className="flex items-center gap-1 md:gap-2">
            <Link to="/workspace">
              <Button variant="ghost" size="sm" className={`nav-btn h-9 px-2 md:px-3 ${location.pathname === "/workspace" ? 'active' : ''}`}>
                <Sparkles className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Tool</span>
              </Button>
            </Link>
            <Link to="/history">
              <Button variant="ghost" size="sm" className={`nav-btn h-9 px-2 md:px-3 ${location.pathname === "/history" ? 'active' : ''}`}>
                <History className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">History</span>
              </Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={onLogout} className="nav-btn h-9 px-2 md:px-3 ml-1 md:ml-2">
              <LogOut className="w-4 h-4" />
            </Button>
          </nav>
        )}
      </div>
    </header>
  );
};

// ==================== MAIN APP ====================
function App() {
  const { tool, credentials, userRole, login, logout } = useAuth();
  const { isConfigAdmin, configCredentials, loginConfig, logoutConfig } = useConfigAuth();
  const [businessUnits, setBusinessUnits] = useState([]);
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState(null);

  useEffect(() => {
    const fetchBusinessUnits = async () => {
      try {
        const res = await axios.get(`${API}/business-units`);
        setBusinessUnits(res.data);
        // Default to Corporate Services UAE
        if (res.data.length > 0) {
          setSelectedBusinessUnit(res.data[0]);
        }
      } catch (err) {
        // Log error for debugging in development only
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to fetch business units:', err.message);
        }
      }
    };
    fetchBusinessUnits();
  }, []);

  const handleSelectBusinessUnit = useCallback((bu) => {
    setSelectedBusinessUnit(bu);
  }, []);

  return (
    <div className="main-container">
      <Toaster position="top-center" toastOptions={TOAST_OPTIONS} />
      <BrowserRouter>
        <Header 
          tool={tool} 
          isConfigAdmin={isConfigAdmin} 
          onLogout={tool ? logout : logoutConfig}
          selectedBusinessUnit={selectedBusinessUnit}
          onSelectBusinessUnit={handleSelectBusinessUnit}
          businessUnits={businessUnits}
        />
        <Routes>
          <Route path="/" element={
            <ToolLoginPage 
              onLogin={login} 
              selectedBusinessUnit={selectedBusinessUnit}
            />
          } />
          <Route path="/workspace" element={
            <WorkspacePage 
              tool={tool} 
              credentials={credentials}
              onLogout={logout} 
            />
          } />
          <Route path="/history" element={
            <ToolHistoryPage 
              tool={tool} 
              credentials={credentials}
              userRole={userRole}
              onLogout={logout} 
            />
          } />
          <Route path="/config" element={<ConfigLoginPage onLogin={loginConfig} />} />
          <Route path="/config/dashboard" element={<ConfigDashboard credentials={configCredentials} onLogout={logoutConfig} />} />
        </Routes>
        <footer className="app-footer px-6 md:px-12 py-6">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-neutral-600 text-xs uppercase tracking-widest">
              © 2026 BlackSwan. All Rights Reserved.
            </p>
          </div>
        </footer>
      </BrowserRouter>
    </div>
  );
}

export default App;
